import React, { useEffect, useRef, useState } from "react";
import { Button, Drawer, Input, Select, Modal, Tag, notification } from "antd";
import Pagination from "@mui/material/Pagination";
import { useLocation, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import moment from "moment";
import TobNavBar from "../../DashBaord/Top_navbar";
import LoadingEffect from "../../../components/Loading/LoadingEffect";
import { getCouponDetails, publishWinner, listWinningCouponCache } from "../../../controllers/V1/hourlyGamesController";
import { sentReVerifyOtp, verifyOtp } from "../../../controllers/accounts/Account";
import "./hourlyAnalyserResponsive.css";
import {userList}  from  "../../../controllers/V1/invoice/invoiceControllers";

export default function ResultAndCouponPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;

  const [resultRows, setResultRows] = useState(state?.resultRows || []);
  const [resultSearchText, setResultSearchText] = useState("");
  const [resultLoading, setResultLoading] = useState(false);
  const [resultTransactionId] = useState(state?.transactionId || "");
  const [selectedHourlyGame] = useState(state?.selectedHourlyGame || {});
  const [hourSlot] = useState(state?.hourSlot || "");
  const [resultPage, setResultPage] = useState(1);
  const [resultPageSize, setResultPageSize] = useState(25);
  const [couponListSortConfig, setCouponListSortConfig] = useState({ key: "", order: "" });

  const [isCouponPopupShow, setIsCouponPopupShow] = useState(false);
  const [couponRows, setCouponRows] = useState([]);
  const [couponColumns, setCouponColumns] = useState([]);
  const [selectedCouponCode, setSelectedCouponCode] = useState("");
  const [couponDetailsCache, setCouponDetailsCache] = useState({});
  const [couponFilterText, setCouponFilterText] = useState("");
  const [winningAmtSortOrder, setWinningAmtSortOrder] = useState("desc");
  const [matchAmountFilters, setMatchAmountFilters] = useState({});
  const [maxWinningAmount, setMaxWinningAmount] = useState("");
  const [matchCountSortOrder, setMatchCountSortOrder] = useState("");
  
  const [posNumberList, setPosNumberList] = useState([]);
  const [appliedPosNumbers, setAppliedPosNumbers] = useState([]);
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
  const [publishOtp, setPublishOtp] = useState("");
  const [otpSubmitting, setOtpSubmitting] = useState(false);
  const publishInFlightRef = useRef(false);
  const isMobileView = viewportWidth <= 767;
  const isTabletView = viewportWidth <= 1024;

  useEffect(() => {
    document.title = "U-Winn CRM || Hourly Coupon Result";
  }, []);
  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toNumber = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : 0;
  };

  const getRowAmount = (row = {}) => {
    const existingAmount = row?.amount ?? row?.total_amount ?? row?.price_amount;
    if (existingAmount !== undefined && existingAmount !== null && existingAmount !== "") {
      return toNumber(existingAmount);
    }
    const qty = row?.qty ?? row?.quantity ?? row?.units ?? row?.count;
    const price = row?.price ?? row?.rate ?? row?.unit_price;
    if (qty !== undefined && price !== undefined) {
      return toNumber(qty) * toNumber(price);
    }
    return 0;
  };

  const doesFilterMatch = (rowValue, operator, inputValue) => {
    if (!operator || inputValue === "" || inputValue === null || inputValue === undefined) {
      return true;
    }

    const value = Number(rowValue ?? 0);
    const target = Number(inputValue);
    if (!Number.isFinite(target)) return true;

    if (operator === "equal_to") return value === target;
    if (operator === "greater_than") return value > target;
    if (operator === "less_than") return value < target;
    return true;
  };

  const normalizeCouponToken = (token = "") => {
    const trimmedToken = String(token).trim();
    if (!trimmedToken) return "";
    const numericToken = Number(trimmedToken);
    return Number.isFinite(numericToken) ? String(numericToken) : trimmedToken.toLowerCase();
  };

  const getCouponTokens = (couponValue = "") => {
    return String(couponValue)
      .split(/[,\-\s]+/)
      .map((token) => token.trim())
      .filter(Boolean);
  };

  const shuffleCouponTokens = (tokens = []) => {
    const shuffled = [...tokens];
    let seed = shuffled.join("").split("").reduce((acc, ch) => acc + ch.charCodeAt(0), 0) || 1;
    const random = () => {
      seed = (seed * 1664525 + 1013904223) >>> 0;
      return seed / 0x100000000;
    };
    for (let index = shuffled.length - 1; index > 0; index -= 1) {
      const swapIndex = Math.floor(random() * (index + 1));
      [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
    }
    return shuffled;
  };

  const formatCouponForDisplay = (couponValue = "") => {
    const tokens = getCouponTokens(couponValue);
    if (!tokens.length) return "";
    return shuffleCouponTokens(tokens).join(",");
  };

  const getCouponTokenFrequency = (couponValue = "") => {
    return getCouponTokens(couponValue)
      .map(normalizeCouponToken)
      .reduce((accumulator, token) => {
        accumulator[token] = (accumulator[token] || 0) + 1;
        return accumulator;
      }, {});
  };

  const getPosTokens = (posValue = "") => {
    return String(posValue)
      .split(",")
      .map((token) => token.trim())
      .filter(Boolean);
  };

  const doesRowMatchPosFilter = (row = {}, posNumbers = []) => {
    if (!posNumbers.length) return true;
    const rowPosTokens = new Set(getPosTokens(row?.pos).map((token) => token.toLowerCase()));
    return posNumbers.some((posNumber) => rowPosTokens.has(String(posNumber).trim().toLowerCase()));
  };

  const isCouponPermutationMatch = (searchText = "", couponValue = "") => {
    const searchFrequency = getCouponTokenFrequency(searchText);
    const couponFrequency = getCouponTokenFrequency(couponValue);
    const searchTokens = Object.keys(searchFrequency);
    const couponTokens = Object.keys(couponFrequency);

    if (!searchTokens.length || !couponTokens.length) return false;
    if (searchTokens.length !== couponTokens.length) return false;

    return searchTokens.every((token) => searchFrequency[token] === couponFrequency[token]);
  };
  const activeMatchNumbers = Array.from(
    { length: selectedHourlyGame?.game_type || 0 },
    (_, index) => index + 1
  ).filter((matchNumber) => selectedHourlyGame?.[`rumble_prize_${matchNumber}`] > 0);

  const filteredResultRows = resultRows.filter((row) => {
    const normalizedSearchText = String(resultSearchText || "").trim().toLowerCase();
    const isGeneralSearchMatch = !normalizedSearchText || Object.values(row || {}).some((value) =>
      String(value ?? "").toLowerCase().includes(normalizedSearchText)
    );
    const isCouponSearchPattern = /[, -]/.test(normalizedSearchText);
    const isPermutationSearchMatch =
      isCouponSearchPattern && isCouponPermutationMatch(normalizedSearchText, row?.ser ?? "");
    const isSearchMatch = isGeneralSearchMatch || isPermutationSearchMatch;
    if (!isSearchMatch) return false;

    const isMatchAmountFilterPassed = activeMatchNumbers.every((matchNumber) => {
      const currentFilter = matchAmountFilters?.[matchNumber] || {};
      return doesFilterMatch(
        row?.[`r${matchNumber}c`],
        currentFilter?.operator,
        currentFilter?.amount
      );
    });
    if (!isMatchAmountFilterPassed) return false;

    if (maxWinningAmount !== "") {
      const totalMatchAmount = Number(row?.ta ?? 0);
      const allowedMax = Number(maxWinningAmount);
      if (Number.isFinite(allowedMax) && totalMatchAmount > allowedMax) {
        return false;
      }
    }

    if (!doesRowMatchPosFilter(row, appliedPosNumbers)) {
      return false;
    }

    return true;
  });
  const sortedResultRows = [...filteredResultRows].sort((a, b) => {
    const { key, order } = couponListSortConfig;
    if (key && order) {
      const valueA = a?.[key];
      const valueB = b?.[key];
      if (key === "ser") {
        const textA = String(valueA ?? "");
        const textB = String(valueB ?? "");
        return order === "asc" ? textA.localeCompare(textB) : textB.localeCompare(textA);
      }
      const numA = Number(valueA ?? 0);
      const numB = Number(valueB ?? 0);
      return order === "asc" ? numA - numB : numB - numA;
    }

    if (matchCountSortOrder) {
      const selectedMatchNumber = Number(selectedHourlyGame?.game_type || 0);
      const countA = Number(a?.[`r${selectedMatchNumber}c`] ?? 0);
      const countB = Number(b?.[`r${selectedMatchNumber}c`] ?? 0);
      return matchCountSortOrder === "asc" ? countA - countB : countB - countA;
    }
    return 0;
  });

  const resultTotalPages = Math.max(1, Math.ceil(sortedResultRows.length / resultPageSize));
  const safeResultPage = Math.min(resultPage, resultTotalPages);
  const paginatedResultRows = sortedResultRows.slice(
    (safeResultPage - 1) * resultPageSize,
    safeResultPage * resultPageSize
  );
  const pageAmountTotal = paginatedResultRows.reduce((sum, row) => sum + getRowAmount(row), 0);
  const overallAmountTotal = sortedResultRows.reduce((sum, row) => sum + getRowAmount(row), 0);

  useEffect(() => {
    setResultPage(1);
  }, [resultSearchText, resultRows, resultPageSize, matchAmountFilters, maxWinningAmount, matchCountSortOrder, appliedPosNumbers]);

  const handleRemovePosNumber = (posNumber) => {
    setPosNumberList((prev) => prev.filter((item) => item !== posNumber));
  };

  const handleApplyPosFilter = () => {
    setAppliedPosNumbers([...posNumberList]);
    setResultPage(1);
  };

  const handleClearPosFilter = () => {
    setSelectedUsers([]);
    setPosNumberList([])
    setPosNumberList([]);
    setAppliedPosNumbers([]);
    setResultPage(1);
  };

  const handleMatchFilterChange = (matchNumber, field, value) => {
    setMatchAmountFilters((prev) => ({
      ...prev,
      [matchNumber]: {
        ...(prev?.[matchNumber] || { operator: "", amount: "" }),
        [field]: value,
      },
    }));
  };

  const filteredCouponRows = couponRows.filter((row) => {
    if (!couponFilterText) return true;
    return Object.values(row || {}).some((value) =>
      String(value ?? "").toLowerCase().includes(couponFilterText.toLowerCase())
    );
  });
  const sortedCouponRows = [...filteredCouponRows].sort((a, b) => {
    if (!winningAmtSortOrder) return 0;
    const amountA = Number(a?.wa ?? 0);
    const amountB = Number(b?.wa ?? 0);
    if (winningAmtSortOrder === "asc") {
      return amountA - amountB;
    }
    return amountB - amountA;
  });
  const totalWinningAmount = sortedCouponRows.reduce(
    (sum, row) => sum + Number(row?.wa ?? 0),
    0
  );
  const totalWinners = sortedCouponRows.length;

  const handleExportResultExcel = () => {
    try {
      if (!sortedResultRows.length) {
        notification.warning({
          message: "No data to export",
          description: "Please load result data before exporting.",
        });
        return;
      }

      const activeMatchNumbers = Array.from(
        { length: selectedHourlyGame?.game_type || 0 },
        (_, index) => index + 1
      ).filter((matchNumber) => selectedHourlyGame?.[`rumble_prize_${matchNumber}`] > 0);

      const exportRows = sortedResultRows.map((row) => {
        const formattedRow = { Coupon: row?.ser ?? "" };
        activeMatchNumbers.forEach((matchNumber) => {
          formattedRow[`Match ${matchNumber}`] = row?.[`r${matchNumber}c`] ?? "";
          formattedRow[`Match ${matchNumber} Amt.`] = row?.[`r${matchNumber}a`] ?? "";
        });
        formattedRow["Total Match Count"] = row?.tmc ?? "";
        formattedRow["Total Match Amt."] = row?.ta ?? "";
        return formattedRow;
      });

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      XLSX.utils.book_append_sheet(workbook, worksheet, "Hourly Result");
      XLSX.writeFile(
        workbook,
        `Hourly-Analysis-Result-${moment().format("YYYY-MM-DD-HH-mm-ss")}.xlsx`
      );
    } catch (error) {
      notification.error({
        message: "Export failed",
        description: error?.message || "Unable to export excel file.",
      });
    }
  };

  const handleCouponClick = async (couponCode = "") => {
    try {
      const normalizedCoupon = String(couponCode || "").replaceAll(",", "-").trim();
      if (!resultTransactionId || !normalizedCoupon) {
        notification.warning({
          message: "Coupon details unavailable",
          description: "Please load result data first and try again.",
        });
        return;
      }
      const cacheKey = `${resultTransactionId}__${normalizedCoupon}`;
      if (couponDetailsCache?.[cacheKey]) {
        const cachedRows = couponDetailsCache[cacheKey];
        setCouponRows(cachedRows);
        setCouponColumns(Object.keys(cachedRows[0] || {}));
        setSelectedCouponCode(String(couponCode || ""));
        setIsCouponPopupShow(true);
        return;
      }
      setResultLoading(true);
      const detailsRes = await getCouponDetails({
        transactionid: resultTransactionId,
        coupon: couponCode,
      });
      if (detailsRes?.status !== true) {
        throw new Error(detailsRes?.message || "Unable to fetch coupon details.");
      }
      const parsedDetailsRows = Array.isArray(detailsRes?.result) ? detailsRes.result : [];
      if (!parsedDetailsRows.length) {
        notification.warning({
          message: "No details found",
          description: `No records available for coupon ${couponCode}.`,
        });
        return;
      }
      setCouponDetailsCache((prev) => ({ ...prev, [cacheKey]: parsedDetailsRows }));
      setCouponRows(parsedDetailsRows);
      setCouponColumns(Object.keys(parsedDetailsRows[0] || {}));
      setSelectedCouponCode(String(couponCode || ""));
      setIsCouponPopupShow(true);
    } catch (error) {
      notification.error({
        message: "Unable to load coupon details",
        description: error?.message || "Please try again.",
      });
    } finally {
      setResultLoading(false);
    }
  };
  const handleCouponListSort = (key) => {
    setCouponListSortConfig((prev) => ({
      key,
      order: prev.key !== key ? "asc" : prev.order === "asc" ? "desc" : prev.order === "desc" ? "" : "asc",
    }));
  };
  const handleExportCouponExcel = () => {
    try {
      if (!sortedCouponRows.length) {
        notification.warning({
          message: "No data to export",
          description: "Please open coupon details before exporting.",
        });
        return;
      }
      const exportRows = sortedCouponRows.map((row) => ({
        "TICKET ID": row?.oid ?? "",
        // "SELLER FIRST NAME": row?.store ?? "",
        // "SELLER LAST NAME": row?.seller ?? "",
        // "SELLER MOBILE": row?.mob ?? "",
        "COUPONS": row?.coup ?? "",
        "MATCHING NUMBER": row?.mn ?? "",
        "WINNER TYPE" : `${row?.ml} Match Prize` ?? "",
        "WINNING AMOUNT": row?.wa ?? "",
      }));
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      XLSX.utils.book_append_sheet(workbook, worksheet, "Coupon Details");
      XLSX.writeFile(
        workbook,
        `Coupon-Details-${selectedCouponCode || "Export"}-${moment().format("YYYY-MM-DD-HH-mm-ss")}.xlsx`
      );
    } catch (error) {
      notification.error({
        message: "Export failed",
        description: error?.message || "Unable to export excel file.",
      });
    }
  };

  const buildPublishPayload = () => {
    const drawResultTime = `${String(hourSlot).padStart(2, "0")}:00:00`;
    const payload = sortedCouponRows.map((row) => ({
      products_id: selectedHourlyGame?.products_id,
      product_name: selectedHourlyGame?.title,
      draw_result_time: drawResultTime,
      draw_result: String(row?.mn ?? "").trim(),
      order_id: String(row?.oid ?? "").trim(),
      coupon_code: String(row?.coup ?? "").trim(),
      matching_coupons: String(row?.mn ?? "").trim(),
      winner_type: `${row?.ml ?? 0} Match Prize`,
      winning_amount: Number(row?.wa ?? 0),
    }));
    return payload.filter((item) => item.coupon_code && item.matching_coupons);
  };

  const executePublishWinner = async () => {
    const validPayload = buildPublishPayload();
    if (!validPayload.length) {
      throw new Error("Invalid winner data. Please reload coupon details and try again.");
    }
    const res = await publishWinner({
      param: validPayload,
      game_name: selectedHourlyGame?.title,
      winning_coupon: selectedCouponCode || ""
    });
    if (res?.status !== true) {
      throw new Error(res?.message || "Failed to publish winners.");
    }
    return res;
  };

  const normalizeWinningCoupon = (coupon = "") => {
    return String(coupon)
      .split(/[,\-\s]+/)
      .map((token) => {
        const trimmed = String(token).trim();
        if (!trimmed) return "";
        const numeric = Number(trimmed);
        return Number.isFinite(numeric) ? String(numeric) : trimmed.toLowerCase();
      })
      .filter(Boolean)
      .sort()
      .join(",");
  };

  const isSameWinningCouponInCache = async () => {
    const currentCoupon = normalizeWinningCoupon(selectedCouponCode);
    if (!currentCoupon) {
      return false;
    }
    const cacheRes = await listWinningCouponCache();
    if (cacheRes?.status !== true) {
      return false;
    }
    const gameName = selectedHourlyGame?.title || "hourly";
    return (cacheRes?.result || []).some((row) => {
      const cachedCoupon = normalizeWinningCoupon(row?.normalized_coupon || row?.winning_coupon || "");
      const cachedGame = String(row?.game_name || "").trim();
      return cachedCoupon === currentCoupon && (!cachedGame || cachedGame === gameName);
    });
  };

  const startOtpPublishFlow = async () => {
    setResultLoading(true);
    const otpRes = await sentReVerifyOtp();
    if (otpRes?.status !== true) {
      throw new Error(otpRes?.message || "Unable to send OTP.");
    }
    setPublishOtp("");
    setIsOtpModalOpen(true);
    notification.success({
      message: "OTP sent",
      description: "Enter the OTP to verify 2FA and publish winners.",
    });
  };

  const handlePublishWinner = async () => {
    try {
      if (!sortedCouponRows.length) {
        notification.warning({
          message: "No data to publish",
          description: "Please open coupon details before publishing.",
        });
        return;
      }
      setResultLoading(true);
      const alreadyAnnounced = await isSameWinningCouponInCache();
      setResultLoading(false);

      if (alreadyAnnounced) {
        Modal.confirm({
          title: "Same winning coupon announced",
          content: "This winning coupon has already been announced today. Are you sure you want to announce the same coupon again?",
          okText: "Confirm",
          cancelText: "Cancel",
          onOk: async () => {
            try {
              await startOtpPublishFlow();
            } catch (error) {
              notification.error({
                message: "2FA required",
                description: error?.message || "Please try again.",
              });
            } finally {
              setResultLoading(false);
            }
          },
          onCancel: () => {
            // Cancel handled on frontend — publish is not started.
          },
        });
        return;
      }

      await startOtpPublishFlow();
    } catch (error) {
      notification.error({
        message: "2FA required",
        description: error?.message || "Please try again.",
      });
    } finally {
      setResultLoading(false);
    }
  };

  const handleVerifyOtpAndPublish = async () => {
    if (publishInFlightRef.current) {
      return;
    }
    try {
      const normalizedOtp = String(publishOtp || "").trim();
      if (!/^\d{6}$/.test(normalizedOtp)) {
        notification.warning({
          message: "Invalid OTP",
          description: "Please enter a valid 6-digit OTP.",
        });
        return;
      }
      publishInFlightRef.current = true;
      setOtpSubmitting(true);
      const verifyRes = await verifyOtp({ otp: Number(normalizedOtp) });
      if (verifyRes?.status !== true) {
        throw new Error(verifyRes?.message || "OTP verification failed.");
      }
      setResultLoading(true);
      const publishRes = await executePublishWinner();
      setIsOtpModalOpen(false);
      setPublishOtp("");
      Modal.success({
        title: "Publish completed",
        content: publishRes?.message || "Winners have been published.",
        okText: "OK",
        onOk: () => navigate("/hourly-analyser"),
      });
    } catch (error) {
      notification.error({
        message: "Publish failed",
        description: error?.message || "Please try again.",
      });
    } finally {
      publishInFlightRef.current = false;
      setOtpSubmitting(false);
      setResultLoading(false);
    }
  };

  const formatHourly = (str) => {
    const [prefix, start, end] = str.split("-");
  
    const formatDateTime = (dt) => {
      const day = dt.substring(0, 2);
      const month = dt.substring(2, 4);
      const year = dt.substring(4, 8);
      const hour = dt.substring(8, 10);
      const min = dt.substring(10, 12);
  
      return `${day}-${month}-${year} ${hour}:${min}`;
    };
  
    return `${prefix}-${formatDateTime(start)} to ${formatDateTime(end)}`;
  };

  const [ALLUSERSLIST, setAllUsersList] = useState([]);
  const [selectedUser, setSelectedUser] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [OPTIONS, setOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [search_by, setSearchBy] = useState('');
  const handleSearchBy = (e)=>setSearchBy(e.target.value);

  const getUsersData = async (searchText='') =>{
      try {
        // Don't clear selected users when searching
        let searchString = searchText;
        let store_name, users_name, users_mobile, users_email;
  
        if(search_by === 'store_name'){
          store_name = { $regex: searchString, $options: "i" };
        } else if(search_by === 'users_name'){
          users_name = { $regex: searchString, $options: "i" };
        } else if(search_by === 'users_mobile'){
          users_mobile = parseInt(searchText);
        } else if(search_by === 'users_email'){
          users_email = { $regex: searchString, $options: "i" };
        } 
  
        const option = {
          condition : { 
            status : "A",
            users_type : 'Retailer'
          }, 
          select : { users_name : true, last_name : true, users_mobile : true, users_mobile : true, store_name : true, users_id : true, pos_number : true, area : true },
          sort : {store_name : 1},
          limit : 4000
        }
        const res = await userList(option);
        if(res.status === true){
          setAllUsersList(res?.result || []);
        }
        setOptionsLoading(false);
      } catch (error) {
        
      }
    }
    
    useEffect(() => {
      getUsersData();
      document.title = `CRM U-Winn || Create Request`;
    }, []);
  
    const debounceTimeout = useRef(null);
  
    const handleSearch = (value) => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      debounceTimeout.current = setTimeout(() => {
        if (value.trim() !== "" && value.length > 3) {
          getUsersData(value);
        }
      }, 500); 
    };
  
    useEffect(()=>{
      if(ALLUSERSLIST.length > 0){
        const transformedArray = ALLUSERSLIST.map(user => ({
          label: `${user.users_name} | ${user.pos_number} | ${user.users_mobile}`,
          value: JSON.stringify(user) 
        }));
        setOptions(transformedArray);
      }
    },[ALLUSERSLIST]);
  
    const handleUserChange = (values) =>{
      try {
        if (values && values.length > 0) {
          const usersData = values.map(value => JSON.parse(value));
          const posNumbers = usersData.map(user => user.pos_number).filter(Boolean);
          setSelectedUsers(usersData);
          // Keep the first selected user for backward compatibility with form fields
          if (usersData.length > 0) {
            setPosNumberList(posNumbers);
            const firstUser = usersData[0];
            setSelectedUser(firstUser);
          }
        } else {
          setSelectedUsers([]);
          setSelectedUser([]);
        }
      } catch (error) {
        
      }
    }


    // Reset form function
  const handleReset = () => {
    // Reset selected users
    setSelectedUsers([]);
    setSelectedUser([]);
    
  };
    
  // Custom tag renderer to show only first 3 users, rest as +X
  const tagRender = (props) => {
    const { label, value, closable, onClose } = props;
    const currentValue = value;
    const index = selectedUsers.findIndex(user => JSON.stringify(user) === currentValue);
    
    // Show only first 3 tags
    if (index < 3) {
      // Extract user name from label (format: "Name | Store | Mobile")
      const displayLabel = label ? label.split(' | ')[0] : label;
      return (
        <span
          style={{
            display: 'inline-block',
            margin: '2px 4px',
            padding: '2px 8px',
            background: '#f0f0f0',
            border: '1px solid #d9d9d9',
            borderRadius: '4px',
            fontSize: '12px'
          }}
        >
          {displayLabel}
          {closable && (
            <span
              onClick={onClose}
              style={{
                marginLeft: '4px',
                cursor: 'pointer',
                color: '#999'
              }}
            >
              ×
            </span>
          )}
        </span>
      );
    }
    
    // For the 4th item, show +X format
    if (index === 3) {
      const remainingCount = selectedUsers.length - 3;
      return (
        <span
          style={{
            display: 'inline-block',
            margin: '2px 4px',
            padding: '2px 8px',
            background: '#f0f0f0',
            border: '1px solid #d9d9d9',
            borderRadius: '4px',
            fontSize: '12px',
            cursor: 'default'
          }}
        >
          +{remainingCount}
        </span>
      );
    }
    
    // Don't render tags after the 4th one
    return null;
  };

  const handleAmountSortToggle = (key, order) => {
    try {
      setWinningAmtSortOrder(order);
      setCouponListSortConfig((prev) => ({
        key,
        order: order,
      }));
    } catch (error) {
      console.error("Error toggling winning amount sort order:", error);
    }
  }

  return (
    <>
      <LoadingEffect isLoading={resultLoading} text="Loading result data..." />
      <div className="container-fluid hourly-page-container">
        <TobNavBar title={"Hourly Coupon Result"} />
        <div className="card shadow mb-4">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3 hourly-toolbar-wrap">
              <div>
                {/* <strong>Transaction:</strong> {formatHourly(resultTransactionId) || "N/A"} */}
              </div>
              <Button onClick={() => navigate("/hourly-analyser")}>Back</Button>
            </div>
            <div className="form-group mb-3">
              <input
                type="text"
                className="form-control store_input_field"
                placeholder="Search in result table"
                value={resultSearchText}
                onChange={(e) => setResultSearchText(e.target.value)}
              />
            </div>
            <div className="row mb-3">
              {/* {activeMatchNumbers.map((matchNumber) => ( */}
              <React.Fragment key={selectedHourlyGame?.game_type}>
                <div className="form-group col-lg-2 col-md-2 col-sm-3 col-xs-12 ">
                  <label htmlFor={`match-filter-operator-${selectedHourlyGame?.game_type}`} className="all_section_for_label_main_class" >
                      Match {selectedHourlyGame?.game_type}:
                  </label>
                  <select
                    name={`match_filter_operator_${selectedHourlyGame?.game_type}`}
                    id={`match-filter-operator-${selectedHourlyGame?.game_type}`}
                    className="form-control store_input_field"
                    value={matchAmountFilters?.[selectedHourlyGame?.game_type]?.operator || ""}
                    onChange={(e) => handleMatchFilterChange(selectedHourlyGame?.game_type, "operator", e.target.value)}
                  >
                    <option value={''}>All</option>
                    <option value={'equal_to'} >Equal to</option>
                    <option value={'greater_than'} >Greater than</option>
                    <option value={'less_than'} >Less than</option>
                  </select>
                </div>
                <div className="form-group col-lg-2 col-md-2 col-sm-3 col-xs-12 ">
                  <label htmlFor={`match-filter-amount-${selectedHourlyGame?.game_type}`} className="all_section_for_label_main_class">Match {selectedHourlyGame?.game_type} Count</label>
                  <input
                    type="number"
                    className="form-control store_input_field"
                    name={`match_filter_amount_${selectedHourlyGame?.game_type}`}
                    id={`match-filter-amount-${selectedHourlyGame?.game_type}`}
                    value={matchAmountFilters?.[selectedHourlyGame?.game_type]?.amount ?? ""}
                    onChange={(e) => handleMatchFilterChange(selectedHourlyGame?.game_type, "amount", e.target.value)}
                  />
                </div>
              </React.Fragment>
              {selectedHourlyGame?.game_type === 6 && (
                <React.Fragment key={selectedHourlyGame?.game_type - 1}>
                  <div className="form-group col-lg-2 col-md-2 col-sm-3 col-xs-12 ">
                    <label htmlFor={`match-filter-operator-${selectedHourlyGame?.game_type - 1}`} className="all_section_for_label_main_class" >
                        Match {selectedHourlyGame?.game_type - 1} :
                    </label>
                    <select
                      name={`match_filter_operator_${selectedHourlyGame?.game_type - 1}`}
                      id={`match-filter-operator-${selectedHourlyGame?.game_type - 1}`}
                      className="form-control store_input_field"
                      value={matchAmountFilters?.[selectedHourlyGame?.game_type - 1]?.operator || ""}
                      onChange={(e) => handleMatchFilterChange(selectedHourlyGame?.game_type - 1, "operator", e.target.value)}
                    >
                      <option value={''}>All</option>
                      <option value={'equal_to'} >Equal to</option>
                      <option value={'greater_than'} >Greater than</option>
                      <option value={'less_than'} >Less than</option>
                    </select>
                  </div>
                  <div className="form-group col-lg-2 col-md-2 col-sm-3 col-xs-12 ">
                    <label htmlFor={`match-filter-amount-${selectedHourlyGame?.game_type - 1}`} className="all_section_for_label_main_class">Match {selectedHourlyGame?.game_type - 1} Count</label>
                    <input
                      type="number"
                      className="form-control store_input_field"
                      name={`match_filter_amount_${selectedHourlyGame?.game_type - 1}`}
                      id={`match-filter-amount-${selectedHourlyGame?.game_type - 1}`}
                      value={matchAmountFilters?.[selectedHourlyGame?.game_type - 1]?.amount ?? ""}
                      onChange={(e) => handleMatchFilterChange(selectedHourlyGame?.game_type - 1, "amount", e.target.value)}
                    />
                  </div>
                </React.Fragment>
              )}
              {/* ))} */}
              <div className="form-group col-lg-2 col-md-2 col-sm-3 col-xs-12 ">
                <label htmlFor="max-winning-amount" className="all_section_for_label_main_class">Approx. Winning Amt</label>
                <input
                  type="number"
                  className="form-control store_input_field"
                  name="max_winning_amount"
                  id="max-winning-amount"
                  value={maxWinningAmount}
                  onChange={(e) => setMaxWinningAmount(e.target.value)}
                />
              </div>
              <div className="form-group col-lg-2 col-md-2 col-sm-3 col-xs-12 ">
                <label htmlFor="match-count-sort-order" className="all_section_for_label_main_class">Sort Order</label>
                <select
                  name="match_count_sort_order"
                  id="match-count-sort-order"
                  className="form-control store_input_field"
                  value={winningAmtSortOrder}
                  // onChange={(e) => setWinningAmtSortOrder(e.target.value)}
                  onChange={(e) => handleAmountSortToggle('ta',e.target.value)}
                >
                  <option value="">None</option>
                  <option value="asc">Count: Low to High</option>
                  <option value="desc">Count: High to Low</option>
                </select>
              </div>
            </div>

            <div className="hourly-pos-filter-section mb-3">
              <div className="hourly-pos-filter-header">
                <div>
                  <strong className="hourly-pos-filter-title">POS Number Filter</strong>
                  <span className="hourly-pos-filter-subtitle">
                    Add up to 15 POS numbers (4 digits each), then click Filter
                  </span>
                </div>
                <span className="hourly-pos-filter-count">{posNumberList.length}/15 added</span>
              </div>
              <div className="row hourly-pos-filter-body">
                <div className="form-group col-lg-12 col-md-12 col-sm-12 col-xs-12 mb-2 mb-md-0">
                  <label htmlFor="pos-number-input" className="all_section_for_label_main_class">
                    POS Number
                  </label>
                  <div className="d-flex hourly-pos-filter-input-row">
                    <Select
                      className="form-control"
                      mode="multiple"
                      onSearch={handleSearch}
                      onChange={handleUserChange}
                      showSearch={true}
                      defaultOpen={true}
                      autoClearSearchValue={false}
                      placeholder="Search to Select"
                      notFoundContent={null}
                      loading={optionsLoading}
                      options={OPTIONS}
                      value={selectedUsers.map(user => JSON.stringify(user))}
                      tagRender={tagRender}
                    />
                    {/* <Button onClick={handleAddPosNumber} disabled={posNumberList.length >= 15}>
                      Add
                    </Button> */}
                  </div>
                </div>
                {/* <div className="form-group col-lg-12 col-md-12 col-sm-12 col-xs-12 mb-2 mb-md-0">
                  <label className="all_section_for_label_main_class">Selected POS Numbers</label>
                  <div className="hourly-pos-filter-tags">
                    {posNumberList.length > 0 ? (
                      posNumberList.map((posNumber) => (
                        <Tag
                          key={posNumber}
                          closable
                          onClose={() => handleRemovePosNumber(posNumber)}
                        >
                          {posNumber}
                        </Tag>
                      ))
                    ) : (
                      <span className="hourly-pos-filter-empty">No POS numbers added yet</span>
                    )}
                  </div>
                </div> */}
                <div className="form-group col-lg-2 col-md-12 col-sm-12 col-xs-12 hourly-pos-filter-actions">
                  <label className="all_section_for_label_main_class d-none d-lg-block">&nbsp;</label>
                  <div className="d-flex flex-wrap hourly-pos-filter-buttons">
                    <Button type="primary" onClick={handleApplyPosFilter}>
                      Filter
                    </Button>
                    <Button onClick={handleClearPosFilter}>
                      Clear
                    </Button>
                  </div>
                </div>
              </div>
              {appliedPosNumbers.length > 0 ? (
                <div className="hourly-pos-filter-applied">
                  <strong>Applied POS:</strong> {appliedPosNumbers.join(", ")}
                </div>
              ) : null}
            </div>

            <div className="d-flex justify-content-between align-items-center mb-3 hourly-toolbar-wrap">
              
              <div onClick={()=> console.log("paginatedResultRows : ", paginatedResultRows)}>
                <strong>Total rows:</strong> {sortedResultRows.length}
              </div>
              <div>
                <strong>Total Tickets:</strong> {paginatedResultRows[0]?.tkt || 0}
              </div>
              <div>
                <strong onClick={()=>console.log("paginatedResultRows : ", paginatedResultRows)}>Total Sales:</strong> {paginatedResultRows[0]?.tkt * selectedHourlyGame?.price || 0}
              </div>
              <div className="d-flex align-items-center hourly-toolbar-actions" style={{ gap: "12px" }}>
                <Button type="primary" onClick={handleExportResultExcel}>
                  Export Excel
                </Button>
                <label htmlFor="resultPageSize" className="mb-0 mr-2">Page Size:</label>
                <select
                  id="resultPageSize"
                  className="form-control"
                  style={{ width: "100px" }}
                  value={resultPageSize}
                  onChange={(e) => setResultPageSize(Number(e.target.value))}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
            {paginatedResultRows.length > 0 ? (
              <>
                <div className="table-responsive hourly-table-scroll">
                  <table className="table table-bordered table-sm hourly-mobile-card-table">
                    <thead>
                      <tr>
                        <th style={{ cursor: "pointer" }} onClick={() => handleCouponListSort("ser")}>
                          Coupon {couponListSortConfig.key === "ser" ? (couponListSortConfig.order === "asc" ? "↑" : couponListSortConfig.order === "desc" ? "↓" : "") : ""}
                        </th>
                        {activeMatchNumbers.map((matchNumber) => (
                          <React.Fragment key={matchNumber}>
                            <th>{`Match ${matchNumber}`}</th>
                            <th>{`Match ${matchNumber} Amt.`}</th>
                          </React.Fragment>
                        ))}
                        <th style={{ cursor: "pointer" }} onClick={() => handleCouponListSort("tmc")}>
                          Total Match Count {couponListSortConfig.key === "tmc" ? (couponListSortConfig.order === "asc" ? "↑" : couponListSortConfig.order === "desc" ? "↓" : "") : ""}
                        </th>
                        <th style={{ cursor: "pointer" }} onClick={() => handleCouponListSort("ta")}>
                          Total Match Amt. {couponListSortConfig.key === "ta" ? (couponListSortConfig.order === "asc" ? "↑" : couponListSortConfig.order === "desc" ? "↓" : "") : ""}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedResultRows.map((row, rowIndex) => (
                        <tr key={rowIndex} >
                          <td data-label="Coupon">
                            <button
                              type="button"
                              className="btn btn-link p-0"
                              onClick={() => handleCouponClick(row?.ser)}
                            >
                              {formatCouponForDisplay(row?.ser)}
                            </button>
                          </td>
                          {activeMatchNumbers.map((matchNumber) => (
                            <React.Fragment key={`${rowIndex}-${matchNumber}`}>
                              <td data-label={`Match ${matchNumber}`}>{row[`r${matchNumber}c`]}</td>
                              <td data-label={`Match ${matchNumber} Amt.`}>{row[`r${matchNumber}a`]}</td>
                            </React.Fragment>
                          ))}
                          <td data-label="Total Match Count">{row?.tmc}</td>
                          <td data-label="Total Match Amt." className="hourly-total-match-amt-cell" style={{ backgroundColor: "#eef4ff" }}>
                            <span style={{ color: "#0d6efd", fontWeight: 700 }}>{row?.ta}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="d-flex justify-content-between align-items-center mt-3 hourly-footer-wrap">
                  <div>
                    <strong>Page Total:</strong> {pageAmountTotal.toFixed(2)} |{" "}
                    <strong>Filtered Total:</strong> {overallAmountTotal.toFixed(2)}
                  </div>
                  <Pagination
                    count={resultTotalPages}
                    page={safeResultPage}
                    onChange={(event, page) => setResultPage(page)}
                  />
                </div>
              </>
            ) : (
              <p>No result data found.</p>
            )}
          </div>
        </div>
      </div>
      <Drawer
        title={
          <div className="d-flex align-items-center flex-wrap gap-2">
            <span>Coupon Details</span>
            {selectedCouponCode ? (
              <>
                <span className="fw-bold">{selectedCouponCode}</span>
                <Button
                  type="primary"
                  size="small"
                  disabled={!selectedCouponCode}
                  onClick={() => setSelectedCouponCode(formatCouponForDisplay(selectedCouponCode))}
                >
                  Shuffle
                </Button>
              </>
            ) : null}
          </div>
        }
        placement="right"
        width={isMobileView ? "100%" : isTabletView ? "92%" : "90%"}
        onClose={() => setIsCouponPopupShow(false)}
        open={isCouponPopupShow}
      >
        <div className="container-fluid px-0 hourly-drawer-content">
          {couponRows.length > 0 && couponColumns.length > 0 ? (
            <div className="table-responsive hourly-table-scroll">
              <div className="form-group mb-2">
                <input
                  type="text"
                  className="form-control store_input_field"
                  placeholder="Filter coupon details"
                  value={couponFilterText}
                  onChange={(e) => setCouponFilterText(e.target.value)}
                />
              </div>
              <div className="d-flex justify-content-between align-items-center mb-2 hourly-toolbar-wrap">
                <div>
                  <strong>Total Winners:</strong> {totalWinners} |{" "}
                  <strong>Total Winning Amt:</strong> {totalWinningAmount.toFixed(2)}
                </div>
                <div className="d-flex" style={{ gap: "8px" }}>
                <Button type="primary" onClick={handleExportCouponExcel}>
                  Export Excel
                </Button>
                <Button type="primary" onClick={handlePublishWinner}>
                  Publish
                </Button>
                </div>
              </div>
              <table className="table table-bordered table-sm hourly-mobile-card-table">
                <thead>
                  <tr>
                    <td>TICKET ID</td>
                    <td>SELLER FIRST NAME</td>
                    <td>SELLER LAST NAME</td>
                    <td>POS Number</td>
                    <td>COUPONS</td>
                    <td
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        setWinningAmtSortOrder((prev) =>
                          prev === "desc" ? "asc" : prev === "asc" ? "" : "desc"
                        )
                      }
                    >
                      WINNING AMT. {winningAmtSortOrder === "asc" ? "↑" : winningAmtSortOrder === "desc" ? "↓" : ""}
                    </td>
                    <td>MATCHING NO.</td>
                  </tr>
                </thead>
                <tbody>
                  {sortedCouponRows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      <td data-label="TICKET ID">{row?.oid}</td>
                      <td data-label="SELLER FIRST NAME">{row?.store}</td>
                      <td data-label="SELLER LAST NAME">{row?.area || row?.seller}</td>
                      <td data-label="SELLER MOBILE">{row?.mob}</td>
                      <td data-label="COUPONS">{row?.coup}</td>
                      <td data-label="WINNING AMT.">{row?.wa}</td>
                      <td data-label="MATCHING NO.">{row?.mn}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!sortedCouponRows.length ? <p className="mt-2">No matching coupon details found.</p> : null}
            </div>
          ) : (
            <p>No coupon details found.</p>
          )}
        </div>
      </Drawer>
      <Modal
        title="2FA Verification"
        open={isOtpModalOpen}
        onCancel={() => {
          if (!otpSubmitting) {
            publishInFlightRef.current = false;
            setIsOtpModalOpen(false);
            setPublishOtp("");
          }
        }}
        onOk={handleVerifyOtpAndPublish}
        okText="Verify & Publish"
        confirmLoading={otpSubmitting}
        okButtonProps={{ disabled: otpSubmitting }}
        cancelButtonProps={{ disabled: otpSubmitting }}
        maskClosable={false}
      >
        <p className="mb-2">Enter the OTP sent to your registered email/mobile to continue publishing winners.</p>
        <Input
          placeholder="Enter 6-digit OTP"
          maxLength={6}
          value={publishOtp}
          disabled={otpSubmitting}
          onChange={(e) => setPublishOtp(e.target.value.replace(/\D/g, ""))}
          onPressEnter={() => {
            if (!otpSubmitting) {
              handleVerifyOtpAndPublish();
            }
          }}
        />
      </Modal>
    </>
  );
}
